import {
  CovideoGTMVehicleItem,
  CovideoGTMVideoAttributes,
  GTMVehicleAttributes,
  GTMVideoAttributes,
  PageLocation,
  PageSection,
} from './types';

/**
 * Convert a duration to decimal minutes.
 * Pass API value (ms or s) and get minutes rounded to 2 decimals.
 *
 * @example toDecimalMinutes(50600, 'ms') // 0.84  (≈ 50.6s)
 * @example toDecimalMinutes(50, 's')     // 0.83  (50s)
 * @example toDecimalMinutes(165, 's')    // 2.75  (2m45s)
 */
export function toDecimalMinutes(
  value: number,
  unit: 'ms' | 's' = 'ms',
  fractionDigits = 2
): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  const seconds = unit === 'ms' ? value / 1000 : value;
  const minutes = seconds / 60;
  const factor = 10 ** fractionDigits;
  return Math.round(minutes * factor) / factor;
}

export function mapToGTMVideoAttributes(
  src?: CovideoGTMVideoAttributes
): GTMVideoAttributes {
  if (!src) {
    return {} as GTMVideoAttributes;
  }

  return {
    video_id: Number(src.videoId),
    video_type: src.videoType ?? undefined,
    video_customer_name: Boolean(src.videoCustomerName),
    video_length: toDecimalMinutes(Number(src.videoLength)) || 0,
    video_title: src.videoTitle || undefined,
  };
}

/**
 * Map app-level meeting attributes to GTM meeting attributes.
 */

export function mapToGtmVehicleAttributes(
  item: CovideoGTMVehicleItem
): GTMVehicleAttributes {
  const vehicleData: GTMVehicleAttributes = !!item
    ? {
        vehicle: item?.title || undefined,
        price: item?.price ? parseFloat(item?.price.toString()) : undefined,
        vin: item?.vin || undefined,
        stock_number: item?.stockNumber?.toString() || undefined,
        condition: item?.itemCondition || undefined,
        engine: item?.engine || undefined,
        make: item?.make || undefined,
        model: item?.model || undefined,
        trim: item?.trim || undefined,
        year: item?.year ? parseInt(item?.year.toString(), 10) : undefined,
        mileage: item?.mileage
          ? parseInt(item?.mileage.toString(), 10)
          : undefined,
        ext_color: item?.exteriorColor || undefined,
        int_color: item?.interiorColor || undefined,
        transmission: item?.transmission || undefined,
        vehicle_type: item?.vehicleType || undefined,
      }
    : {};
  return vehicleData;
}

// deliveryTime format: "YYYY-MM-DD;HH:MM;AM/PM" (example)
// Returns { meetingDate?: string, meetingTime?: string }
export function mapDeliveryTimeToGtmMeeting(deliveryTime?: string) {
  const [date, t1, t2] = (deliveryTime ?? '').split(';');

  const meetingDate = date?.trim() ? date.trim() : undefined;
  const meetingTime =
    t1?.trim() && t2?.trim() ? `${t1.trim()} ${t2.trim()}` : undefined;

  return { meetingDate, meetingTime };
}

export const TRACKED_PAGES = {
  INVENTORY_DETAILS: {
    pageLocation: PageLocation.INVENTORY,
    pageSection: PageSection.DETAILS,
  },
  EMBED_SEND_AND_SHARE: {
    pageLocation: PageLocation.SEND_AND_SHARE,
    pageSection: PageSection.EMBED,
  },
  SEND_SEND_AND_SHARE: {
    pageLocation: PageLocation.SEND_AND_SHARE,
    pageSection: PageSection.SEND,
  },
  SHARE_SEND_AND_SHARE: {
    pageLocation: PageLocation.SEND_AND_SHARE,
    pageSection: PageSection.SHARE,
  },
  MEETING_SCHEDULE: {
    pageLocation: PageLocation.MEETINGS,
    pageSection: PageSection.SCHEDULE,
  },
  MEETING_DETAILS: {
    pageLocation: PageLocation.MEETINGS,
    pageSection: PageSection.DETAILS,
  },
  VIDEOS_RECORD: {
    pageLocation: PageLocation.RECORD,
    pageSection: PageSection.RECORDER,
  },
  VIDEOS_RECORDER: {
    pageLocation: PageLocation.RECORD,
    pageSection: PageSection.RECORDER,
  },
  RECORD_VIDEOS_DETAILS: {
    pageLocation: PageLocation.RECORD,
    pageSection: PageSection.DETAILS,
  },
  VIDEOS_MERGE: {
    pageLocation: PageLocation.MERGE,
    pageSection: PageSection.DETAILS,
  },
  VIDEOS_VOICE_RECORDER: {
    pageSection: PageLocation.DETAILS,
    pageLocation: PageSection.VOICE_RECORDER,
  },
} as const;

interface GetChangedFieldsOptions {
  initialValues: Record<string, any>;
  currentValues: Record<string, any>;
  trackedFields?: string[];
  trackedArrayFields?: Record<string, string[]>;
  onlyChangedList?: boolean;
}

function flattenObject(
  obj: Record<string, any>,
  trackedFields: string[],
  trackedArrayFields: Record<string, string[]>,
  prefix = ''
): Record<string, string> {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      const rootKey = fullKey.split('.')[0];

      // At root level, check if this key (or any tracked field/array) starts with it
      if (!prefix) {
        const isTrackedField = trackedFields.some(
          f => f === key || f.startsWith(`${key}.`)
        );
        const isTrackedArray = key in trackedArrayFields;
        if (!isTrackedField && !isTrackedArray) return acc;
      }

      if (Array.isArray(value)) {
        const fieldsToTrack =
          trackedArrayFields[key] ?? trackedArrayFields[rootKey];
        if (fieldsToTrack) {
          value.forEach((item, index) => {
            fieldsToTrack.forEach(field => {
              acc[`${fullKey}.${index}.${field}`] = String(item[field] ?? '');
            });
          });
        } else {
          acc[fullKey] = String(value);
        }
      } else if (value !== null && typeof value === 'object') {
        if (trackedArrayFields[rootKey]) {
          return acc;
        }

        Object.assign(
          acc,
          flattenObject(value, trackedFields, trackedArrayFields, fullKey)
        );
      } else {
        // Only include leaf value if fullKey exactly matches a tracked field,
        // or if the tracked field is a prefix of fullKey (deeper nesting),
        // or if no dotted tracked fields target this root (plain field)
        const isDottedTarget = trackedFields.some(f => f.includes('.'));
        const isExactOrDeeper = trackedFields.some(
          f => fullKey === f || fullKey.startsWith(`${f}.`)
        );

        if (!isDottedTarget || isExactOrDeeper) {
          acc[fullKey] = String(value ?? '');
        }
      }

      return acc;
    },
    {} as Record<string, string>
  );
}

/**
 * BEFORE MAKING ANY CHANGES:
 * PLEASE READ THIS COMMENT AND THE TESTS IN `test-getChangedFields.ts` TO UNDERSTAND HOW THIS FUNCTION WORKS.
 *
 * Compares `initialValues` vs `currentValues` and returns which tracked fields changed.
 *
 * The function works in two steps:
 * 1) `flattenObject(...)` flattens the input objects into a `{ [path]: string }` map,
 *    but **only for paths you explicitly track** via `trackedFields` / `trackedArrayFields`.
 * 2) `getChangedFields(...)` diffs the two flattened maps and returns:
 *    - `changed_fields`: list of changed field paths
 *    - optionally (`onlyChangedList: false`) `<path>_before` and `<path>_after` values
 *
 * @param initialValues - baseline object (e.g. values on load)
 * @param currentValues - current object (e.g. values after user edits)
 * @param trackedFields - list of field paths to track (supports dot paths)
 *   - Simple field: `"title"`
 *   - Nested field: `"company.name"` (tracks only that nested path)
 * @param trackedArrayFields - map of array field name -> list of item subfields to track
 *   - Example: `{ links: ["linkText", "itemType"] }` tracks `links.<index>.linkText`, etc.
 *   - Note: this is intended for arrays of objects at the root key (or within that root branch)
 * @param onlyChangedList - when true, return only `changed_fields`
 *
 * @returns An object containing:
 *  - `changed_fields: string[]`
 *  - and when `onlyChangedList` is false:
 *    - `<path>_before: string`
 *    - `<path>_after: string`
 *
 * @example
 * // 1) Simple key/value pairs
 * const res = getChangedFields({
 *   initialValues: { title: "A", company: "X", ignored: 123 },
 *   currentValues: { title: "B", company: "X", ignored: 999 },
 *   trackedFields: ["title", "company"],
 *   onlyChangedList: false,
 * });
 *
 * // res.changed_fields -> ["title"]
 * // res.title_before   -> "A"
 * // res.title_after    -> "B"
 *
 * @example
 * // 2) Nested object fields (dot paths)
 * // Track specific nested properties with `trackedFields`:
 * const res = getChangedFields({
 *   initialValues: {
 *     videoSource: { label: "OBS Virtual Camera", value: "abc" },
 *   },
 *   currentValues: {
 *     videoSource: { label: "FaceTime HD Camera", value: "def" },
 *   },
 *   trackedFields: ["videoSource.label"],
 *   onlyChangedList: true,
 * });
 *
 * // res.changed_fields -> ["videoSource.label"]
 *
 * @example
 * // 3) Array of objects
 * // Track specific subfields on each array item using `trackedArrayFields`.
 * const res = getChangedFields({
 *   initialValues,
 *   currentValues: data,
 *   trackedFields: ["title", "company"],
 *   trackedArrayFields: {
 *     links: ["linkText", "itemType", "fileThumbnail", "newWindow"],
 *   },
 *   onlyChangedList: false,
 * });
 *
 * // If `links` changes, paths look like:
 * // "links.0.linkText", "links.2.newWindow", ...
 * // and you get before/after fields for each changed path.
 */
export function getChangedFields({
  initialValues,
  currentValues,
  trackedFields = [],
  trackedArrayFields = {},
  onlyChangedList = false,
}: GetChangedFieldsOptions) {
  const flatInitial = flattenObject(
    initialValues,
    trackedFields,
    trackedArrayFields
  );
  const flatCurrent = flattenObject(
    currentValues,
    trackedFields,
    trackedArrayFields
  );

  const changedFields: string[] = [];
  const result: Record<string, string | string[]> = {};

  for (const key in flatCurrent) {
    if (flatInitial[key] !== flatCurrent[key]) {
      changedFields.push(key);

      if (!onlyChangedList) {
        result[`${key}_before`] = flatInitial[key] ?? '';
        result[`${key}_after`] = flatCurrent[key];
      }
    }
  }

  return {
    changed_fields: changedFields,
    ...result,
  };
}
